import React from 'react';
import { Settings, Minus, Plus, Trash2, ShieldAlert } from 'lucide-react';

const SettingStepper = ({ label, hint, value, onChange, min, max, testId }) => (
  <div className="flex items-center justify-between py-1">
    <div>
      <p className="text-sm font-bold text-white">{label}</p>
      {hint && <p className="text-[11px] text-gray-500 mt-0.5">{hint}</p>}
    </div>
    <div className="flex items-center space-x-4 bg-[#1C1C1E] p-1.5 rounded-xl border border-gray-800">
      <button data-testid={`${testId}-minus`} onClick={() => onChange(Math.max(min, value - 1))} className="p-2 text-[#00BFFF] hover:bg-gray-800 rounded-lg"><Minus size={16} /></button>
      <span data-testid={testId} className="text-sm font-bold w-7 text-center">{value}</span>
      <button data-testid={`${testId}-plus`} onClick={() => onChange(Math.min(max, value + 1))} className="p-2 text-[#00BFFF] hover:bg-gray-800 rounded-lg"><Plus size={16} /></button>
    </div>
  </div>
);

const Toggle = ({ label, hint, value, onChange, testId }) => (
  <div className="flex items-center justify-between py-1">
    <div className="pr-4">
      <p className="text-sm font-bold text-white">{label}</p>
      {hint && <p className="text-[11px] text-gray-500 mt-0.5">{hint}</p>}
    </div>
    <button
      data-testid={testId}
      onClick={() => onChange(!value)}
      className={`w-12 h-7 rounded-full p-1 transition-colors shrink-0 ${value ? 'bg-[#00BFFF]' : 'bg-gray-700'}`}
    >
      <div className={`w-5 h-5 rounded-full bg-white transition-transform ${value ? 'translate-x-5' : 'translate-x-0'}`} />
    </button>
  </div>
);

export default function SettingsView({ settings, onChange, onClearHistory, matchCount, BottomNav }) {
  const update = (key, val) => onChange({ ...settings, [key]: val });

  return (
    <div className="flex flex-col h-full bg-[#0A0A0A] text-white">
      <div className="p-6 border-b border-gray-800 flex items-center gap-3">
        <Settings className="w-5 h-5 text-[#00BFFF]" />
        <h1 className="text-xl font-bold tracking-wider">SETTINGS</h1>
      </div>

      <div className="flex-1 p-5 space-y-6 overflow-y-auto">
        <div>
          <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-3">Match Defaults</p>
          <div className="bg-[#1C1C1E] border border-gray-800 rounded-2xl p-4 space-y-4 divide-y divide-gray-800/60">
            <SettingStepper testId="set-periods" label="Periods" hint="Default number of periods" value={settings.defaultPeriods} onChange={v => update('defaultPeriods', v)} min={1} max={4} />
            <div className="pt-4"><SettingStepper testId="set-length" label="Period Length" hint="Minutes per period" value={settings.defaultPeriodLength} onChange={v => update('defaultPeriodLength', v)} min={1} max={90} /></div>
            <div className="pt-4"><SettingStepper testId="set-sinbin" label="Sin Bin Duration" hint="Default minutes in the bin" value={settings.defaultSinBin} onChange={v => update('defaultSinBin', v)} min={1} max={20} /></div>
          </div>
        </div>

        <div>
          <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-3">Behaviour</p>
          <div className="bg-[#1C1C1E] border border-gray-800 rounded-2xl p-4 space-y-4 divide-y divide-gray-800/60">
            <Toggle testId="set-confirm-end" label="Confirm End Match" hint="Ask before finishing a match" value={settings.confirmEndMatch} onChange={v => update('confirmEndMatch', v)} />
            <div className="pt-4"><Toggle testId="set-auto-second-yellow" label="Auto Yellow→Red" hint="Detect second yellow automatically" value={settings.autoSecondYellow} onChange={v => update('autoSecondYellow', v)} /></div>
            <div className="pt-4"><Toggle testId="set-quick-goal" label="Quick Add Goal" hint="Tap team → Goal logs instantly (no player). Great for youth games." value={settings.quickGoal} onChange={v => update('quickGoal', v)} /></div>
          </div>
        </div>

        <div>
          <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest mb-3">Data</p>
          <button
            data-testid="set-clear-history"
            onClick={onClearHistory}
            className="w-full bg-[#1C1C1E] border border-[#FF3B30]/40 text-[#FF3B30] font-bold py-4 rounded-2xl flex items-center justify-center gap-2"
          >
            <Trash2 size={16} /> Clear All Match History ({matchCount})
          </button>
        </div>

        <div className="bg-[#1C1C1E] border border-gray-800 rounded-2xl p-6 flex flex-col items-center text-center">
          <ShieldAlert className="w-10 h-10 text-[#00BFFF] mb-2" />
          <h2 className="text-base font-bold">Tournevo</h2>
          <p className="text-xs text-gray-500 mt-1">Match-day control for referees • v1.0</p>
        </div>
      </div>
      {BottomNav}
    </div>
  );
}
